import dayjs from 'dayjs';
import { FC, Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
	Button,
	Col,
	Form,
	Input,
	Row,
	Select,
	Space,
	Table,
	TableColumnType,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import FleetTitle from '@/libs/antd/components/Title';
import { FleetPagination, FleetQueryParams } from '@/libs/fleet-api/fetcher';
import {
	CustomerGroup,
	getCustomerGroups,
} from '@/libs/fleet-api/services/customer';
import { useModals } from '@/components/modals/ModalProvider';
import GenericErrorMessage from '@/components/GenericErrorMessage';
import { formatCurrency } from '@/libs/antd/formater';
import { useApp } from '@/App';

const FRMCustomerGroupListPage: FC = () => {
	const { navigate } = useApp();
	const { openCustomerGroupDetail } = useModals();
	const [searchBy, setSearchBy] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState<string | null>(null);
	const [pagination, setPagination] = useState<FleetPagination>({
		pageNumber: 1,
		pageSize: 20,
	});

	const [queryParams, setQueryParams] = useState<FleetQueryParams>({
		search: undefined,
		filter: undefined,
		pagination,
	});

	const { data, isLoading, isFetching, isError } = useQuery({
		queryKey: ['getCustomerGroups', queryParams] as const,
		queryFn: ({ queryKey }) => {
			const [, queryParams] = queryKey;
			return getCustomerGroups(queryParams);
		},
	});

	if (isError) return <GenericErrorMessage />;

	const dataSource = data?.data.items ?? [];

	const handleSearch = () => {
		setQueryParams((current) => {
			if (!searchBy || !searchValue) {
				return {
					...current,
					search: undefined,
				};
			}
			return {
				...current,
				search: {
					by: searchBy,
					value: searchValue,
				},
				pagination: {
					pageNumber: 1,
					pageSize: 20,
				},
			};
		});
	};

	const columns: TableColumnType<CustomerGroup>[] = [
		{
			title: 'Edit',
			width: 1,
			align: 'center',
			render: (_, record) => (
				<a
					onClick={(e) => {
						e.preventDefault();
						navigate('EditCustomerGroupPage', {
							groupId: String(record.groupId!),
						});
					}}
				>
					<EditOutlined />
				</a>
			),
		},
		{
			title: 'Group ID',
			dataIndex: ['groupId'],
		},
		{
			title: 'Group Name',
			render: (_, record) => (
				<a
					onClick={(e) => {
						e.preventDefault();
						openCustomerGroupDetail(record.groupId!);
					}}
				>
					{record.groupName}
				</a>
			),
		},
		{
			title: 'Total Exposure',
			align: 'right',
			render: (_, record) =>
				record.totalExposureAmount ? formatCurrency(record.totalExposureAmount) : '-',
		},
		{
			title: 'Total NTF',
			align: 'right',
			render: (_, record) =>
				record.totalNtf ? formatCurrency(record.totalNtf) : '-',
		},
		{
			title: 'Total Outstanding',
			align: 'right',
			render: (_, record) =>
				record.totalOutstanding ? formatCurrency(record.totalOutstanding) : '-',
		},
		{
			title: 'Total Sisa Limit',
			align: 'right',
			render: (_, record) =>
				record.totalRemainingPlafon
					? formatCurrency(record.totalRemainingPlafon)
					: '-',
		},
		{
			title: 'Start Date',
			render: (_, record) =>
				record.startDate
					? dayjs(record.startDate).format('DD/MM/YYYY')
					: '-',
		},
	];

	return (
		<Fragment>
			<Space
				direction="vertical"
				style={{ display: 'flex' }}
				size={'large'}
			>
				<FleetTitle>Group Customer BMPP</FleetTitle>

				<Form
					layout="horizontal"
					colon={false}
					labelAlign="left"
					onFinish={handleSearch}
				>
					<Row gutter={[20, 20]}>
						<Col flex="auto">
							<Form.Item
								label={<span>Search By</span>}
								labelCol={{ flex: 'auto' }}
								wrapperCol={{ flex: 'none', style: { width: 250 } }}
							>
								<Select
									placeholder="Search By"
									options={[
										{
											label: 'Nama Group',
											value: 'group-name',
										},
										{
											label: 'Group ID',
											value: 'group-id',
										},
									]}
									allowClear
									onChange={(value) => {
										setSearchBy(value);
									}}
									onClear={() => {
										setQueryParams((current) => {
											return {
												...current,
												search: undefined,
											};
										});
										setSearchBy(null);
									}}
									style={{
										display: 'block',
										width: '100%',
									}}
								/>
							</Form.Item>
						</Col>
						<Col flex="auto">
							<Form.Item>
								<Input
									placeholder="Search..."
									style={{
										display: 'block',
										width: '100%',
									}}
									disabled={searchBy === null}
									value={searchValue!}
									onChange={(e) => {
										const value = e.target.value;
										setSearchValue(value);
									}}
								/>
							</Form.Item>
						</Col>
						<Col style={{ width: 220 }}>
							<Form.Item>
								<Button
									block
									type="primary"
									htmlType="submit"
								>
									SEARCH
								</Button>
							</Form.Item>
						</Col>
					</Row>
				</Form>

				<Table
					bordered
					scroll={{
						x: 1200,
					}}
					loading={isLoading || isFetching}
					dataSource={dataSource.map((data, key) => {
						return { ...data, key };
					})}
					columns={columns}
					pagination={{
						total: data?.data.totalCount,
						current: pagination.pageNumber,
						pageSize: pagination.pageSize,
						onChange: (pageNumber, pageSize) => {
							setPagination((current) => {
								return {
									pageSize,
									pageNumber: current.pageSize !== pageSize ? 1 : pageNumber,
								};
							});
							setQueryParams((current) => {
								return {
									...current,
									pagination: {
										pageSize,
										pageNumber:
											current.pagination?.pageSize !== pageSize
												? 1
												: pageNumber,
									},
								};
							});
						},
					}}
				/>

				<Button
					type="primary"
					disabled={false}
					style={{
						width: 200,
						display: 'block',
						marginLeft: 'auto',
					}}
					onClick={() => {
						navigate('CreateCustomerGroupPage');
					}}
				>
					ADD
				</Button>

			</Space>
		</Fragment>
	);
};

export default FRMCustomerGroupListPage;
